import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecipe, deleteRecipe } from '../../../../api/healthAPI';
import MealTile from '../../../../component/HealthComponent/MealTile';
import ConfirmPopup from '../../../../component/common/modals/ConfirmPopup';
import { errorToast, successToast } from '../../../../utils/helper';

const NutritionMeals = ({ searchKey }) => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 10
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Fetch meals on component mount and when searchKey changes
  useEffect(() => {
    fetchMeals();
  }, [searchKey]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchMeals = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 6, // Limit to 6 items for dashboard view
        status: 'published', // Only fetch published meals for dashboard
        ...(searchKey && { search: searchKey })
      };
      const response = await getRecipe(params);
      console.log('Meals response:', response);

      if (response?.data?.recipes) {
        // reuse workouts endpoint for meals data until a meals API is available
        setMeals(response.data.recipes);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching meals:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleMealClick = (meal) => {
    console.log('Meal clicked:', meal);
    navigate(`/health/recipe/new?id=${meal._id}`, {
      state: { _id: meal._id, action: 'edit' }
    });
  };

  const handleMenuClick = (e, meal) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === meal._id ? null : meal._id);
  };

  const handleView = (meal) => {
    navigate(`/health/recipe/${meal._id}`, {
      state: { _id: meal._id, action: 'view' }
    });
    setOpenMenuId(null);
  };

  const handleEdit = (meal) => {
    navigate(`/health/recipe/new?id=${meal._id}`, {
      state: { _id: meal._id, action: 'edit' }
    });
    setOpenMenuId(null);
  };

  const handleDelete = (meal) => {
    setMealToDelete(meal);
    setShowDeleteConfirm(true);
    setOpenMenuId(null);
  };

  const confirmDelete = async () => {
    if (!mealToDelete) return;

    try {
      setIsDeleting(true);
      const response = await deleteRecipe(mealToDelete._id);

      if (response?.meta?.code === 1) {
        successToast('Recipe deleted successfully');
        fetchMeals();
      } else {
        throw new Error(response?.meta?.message || 'Failed to delete recipe');
      }
    } catch (error) {
      console.error('Error deleting meal:', error);
      errorToast(error.message || 'Failed to delete recipe');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setMealToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setMealToDelete(null);
  };

  const handleViewAll = () => {
    navigate('/health/recipes');
  };
  return (
    <div className="bg-white mt-4 rounded-lg p-6">
      {/* Header with View All button */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Meals</h2>
        <button
          onClick={handleViewAll}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Workout Grid */}
      {!loading && meals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {meals.map((meal) => (
            <div key={meal._id} className="relative">
              <MealTile
                image={meal.image || meal.image_url || '/assets/meals/default.jpg'}
                mealName={meal.meal_name || meal.workout_name || meal.title}
                calories={meal.calories || meal.kcal || null}
                badgeText={meal.category || meal.cuisine || 'Meal'}
                onClick={() => handleMealClick(meal)}
                onMenuClick={(e) => handleMenuClick(e, meal)}
              />

              {/* Dropdown Menu */}
              {openMenuId === meal._id && (
                <div className="absolute top-12 right-2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
                  <button
                    onClick={() => handleView(meal)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={() => handleEdit(meal)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(meal)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && meals.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No meals</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new meal.</p>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={confirmDelete}
        handleNo={handleDeleteCancel}
        message={`Are you sure you want to delete "${
          mealToDelete?.title || mealToDelete?.meal_name
        }"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default NutritionMeals;
